import type { IClient } from '@/entities/client.entity';
import { cn } from '@/lib/utils';
import {
	ActivityIcon,
	BotIcon,
	CalendarClockIcon,
	ChevronDownIcon,
	ClockIcon,
	ContainerIcon,
	CpuIcon,
	GlobeIcon,
	HashIcon,
	LayoutGridIcon,
	SmartphoneIcon,
	UserIcon,
	UsersIcon,
} from 'lucide-react';
import { useState } from 'react';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);

interface ClientDeviceMetaProps {
	client: IClient;
	onCopy: (text: string) => void;
	defaultOpen?: boolean;
}

export default function ClientDeviceMeta({ client, onCopy, defaultOpen = false }: ClientDeviceMetaProps) {
	const [open, setOpen] = useState(defaultOpen);

	return (
		<div className='grid gap-1'>
			<button
				type='button'
				onClick={() => setOpen(v => !v)}
				className='flex items-center gap-1 h-7 text-sm text-muted-foreground hover:text-foreground transition-colors'
			>
				<ChevronDownIcon size={14} className={cn('transition-transform', open && 'rotate-180')} />
				Ещё
			</button>
			{open ? (
				<div className='grid gap-1 pb-1'>
					<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy('#' + client.id)}>
						<HashIcon size={14} className='opacity-40 shrink-0' />
						<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.id}</span>
					</button>
					<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy(client.profileNickname)}>
						<UserIcon size={14} className='opacity-40 shrink-0' />
						<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.profileNickname}</span>
					</button>
					<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy(client.buildId)}>
						<ContainerIcon size={14} className='opacity-40 shrink-0' />
						<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.buildId}</span>
					</button>
					<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy(client.sdkVersion)}>
						<LayoutGridIcon size={14} className='opacity-40 shrink-0' />
						<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.sdkVersion}</span>
					</button>
					{client.deviceName ? (
						<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy(client.deviceName!)}>
							<SmartphoneIcon size={14} className='opacity-40 shrink-0' />
							<span className='text-xs text-muted-foreground shrink-0'>Устройство</span>
							<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.deviceName}</span>
						</button>
					) : null}
					{client.brand || client.model ? (
						<button
							type='button'
							className='flex gap-1 items-center text-left'
							onClick={() => onCopy([client.brand, client.model].filter(Boolean).join(' '))}
						>
							<CpuIcon size={14} className='opacity-40 shrink-0' />
							<span className='text-xs text-muted-foreground shrink-0'>Модель</span>
							<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>
								{[client.brand, client.model].filter(Boolean).join(' ')}
							</span>
						</button>
					) : null}
					{client.androidRelease ? (
						<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy(client.androidRelease!)}>
							<BotIcon size={14} className='opacity-40 shrink-0' />
							<span className='text-xs text-muted-foreground shrink-0'>Android</span>
							<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.androidRelease}</span>
						</button>
					) : null}
					{client.locale ? (
						<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy(client.locale!)}>
							<GlobeIcon size={14} className='opacity-40 shrink-0' />
							<span className='text-xs text-muted-foreground shrink-0'>Локаль</span>
							<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.locale}</span>
						</button>
					) : null}
					{client.timezone ? (
						<button type='button' className='flex gap-1 items-center text-left' onClick={() => onCopy(client.timezone!)}>
							<ClockIcon size={14} className='opacity-40 shrink-0' />
							<span className='text-xs text-muted-foreground shrink-0'>Часовой пояс</span>
							<span className='text-sm hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'>{client.timezone}</span>
						</button>
					) : null}
					{client.contactsCount != null ? (
						<div className='flex gap-1 items-center text-left'>
							<UsersIcon size={14} className='opacity-40 shrink-0' />
							<span className='text-xs text-muted-foreground shrink-0'>Контакты</span>
							<span className='text-sm'>{client.contactsCount}</span>
						</div>
					) : null}
					{client.protocolVersion != null ? (
						<div className='flex gap-1 items-center text-left'>
							<ActivityIcon size={14} className='opacity-40 shrink-0' />
							<span className='text-xs text-muted-foreground'>Protocol</span>
							<span className='text-xs rounded-xs bg-muted px-1.5 py-0.5'>v{client.protocolVersion}</span>
						</div>
					) : null}
					<div className='grid gap-0.5 min-w-0'>
						<div className='flex gap-1 items-center'>
							<CalendarClockIcon size={14} className='opacity-40 shrink-0' />
							<span className='text-xs text-muted-foreground'>Установлен</span>
							<span className='text-xs text-chart-2'>
								<TimeAgo date={client.createdAt} formatter={formatter} />
							</span>
						</div>
						<div className='flex gap-1 items-center'>
							<ActivityIcon size={14} className='opacity-40 shrink-0' />
							<span className='text-xs text-muted-foreground'>Ласт пинг</span>
							<span className='text-xs text-chart-2'>
								<TimeAgo date={client.onlineAt} formatter={formatter} />
							</span>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
}
